import { useCallback, useRef, useState } from 'react';
import { MIN_VOLUME_VALUE_ON_UNMUTE_SOUND } from './constants';

interface IUseVolumeControlProps {
  volumePercent: number;
  onChange: (volume: number) => void;
}

interface IUseVolumeControlResult {
  isOpen: boolean;
  handleChangeValue: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleToggleMuteVolume: () => void;
  handleOpen: () => void;
  handleClose: () => void;
}

export const useVolumeControl = ({ volumePercent, onChange }: IUseVolumeControlProps): IUseVolumeControlResult => {
  const previousVolumeValue = useRef(volumePercent);
  const isMuted = useRef(volumePercent < 1);
  const [isOpen, setIsOpen] = useState(false);

  const handleChangeValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      previousVolumeValue.current = parseInt(event.target.value, 10);
      onChange(previousVolumeValue.current);
      isMuted.current = previousVolumeValue.current < 1;
    },
    [onChange],
  );

  const handleToggleMuteVolume = useCallback(() => {
    const newValueIsMuted = !isMuted.current;
    if (newValueIsMuted) {
      onChange(0);
    } else {
      onChange(Math.max(previousVolumeValue.current, MIN_VOLUME_VALUE_ON_UNMUTE_SOUND));
    }
    isMuted.current = newValueIsMuted;
  }, [onChange]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    handleChangeValue,
    handleToggleMuteVolume,
    handleOpen,
    handleClose,
  };
};
